import React, { forwardRef } from 'react';
import { Pill, Syringe, Flame, Clock, Calendar, Building2, Target, CheckCircle2, PauseCircle, FileText, Layers, ShieldAlert } from 'lucide-react';
import { obfuscateSupplierName } from '../utils/exportImage';
import {
  getEffectivePrimaryCategory,
  getEffectiveSecondaryCategory,
  getCategoryConfig
} from '../utils/healthCategories';
import { formatDosageRange } from '../utils/sortUtils';

/**
 * Solid, non-transparent card element for image export.
 * Uses solid background colors (#0f172a / slate-900) instead of semi-transparent glass backdrop filters.
 */
function OpaqueExportCard({ protocol, obfuscateSupplier = false }) {
  const {
    name,
    category,
    weeklyTotalDosage,
    unit,
    administrationFormat,
    timingOfDay,
    frequencySchedule,
    brandOrSupplier,
    intendedOutcome,
    notes,
    cycleStatus,
    startDate,
    endDate
  } = protocol;

  const formatDosage = () => formatDosageRange(protocol);

  const getStatusBadge = () => {
    switch (cycleStatus) {
      case 'Active':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-950 text-emerald-300 border border-emerald-500/30">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
            <span>Active</span>
          </span>
        );
      case 'Pending':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-950 text-blue-300 border border-blue-500/30">
            <Clock className="w-3 h-3" />
            <span>Pending</span>
          </span>
        );
      case 'Paused':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-950 text-amber-300 border border-amber-500/30">
            <PauseCircle className="w-3 h-3" />
            <span>Paused</span>
          </span>
        );
      case 'Completed':
        return (
          <span className="inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-slate-800 text-slate-300 border border-slate-700">
            <CheckCircle2 className="w-3 h-3" />
            <span>Completed</span>
          </span>
        );
      default:
        return null;
    }
  };

  const getCategoryBadge = () => {
    switch (category) {
      case 'Peptide':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-purple-950 text-purple-300 border border-purple-500/40">
            <Syringe className="w-3 h-3 mr-1" />
            {category}
          </span>
        );
      case 'Hormones & AAS':
      case 'Hormone':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-rose-950 text-rose-300 border border-rose-500/40">
            <Flame className="w-3 h-3 mr-1" />
            Hormones & AAS
          </span>
        );
      case 'Supplement':
      default:
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase bg-sky-950 text-sky-300 border border-sky-500/40">
            <Pill className="w-3 h-3 mr-1" />
            {category}
          </span>
        );
    }
  };

  return (
    <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-5 flex flex-col justify-between text-left shadow-lg">
      <div>
        {/* Header Badges */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center space-x-2">
            {getCategoryBadge()}
            {getStatusBadge()}
          </div>
        </div>

        {/* Title & Health Categories */}
        <h3 className="text-base font-bold text-white mb-1.5">{name}</h3>

        {/* Health Target Badges */}
        {(() => {
          const primaryCat = getEffectivePrimaryCategory(protocol);
          const secondaryCat = getEffectiveSecondaryCategory(protocol);
          const primaryConfig = getCategoryConfig(primaryCat);
          return (
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold tracking-wide ${primaryConfig.badgeClass}`}>
                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${primaryConfig.dotClass}`} />
                {primaryCat}
              </span>
              {secondaryCat && (
                <span 
                  className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium text-slate-300 bg-slate-900 border border-slate-700/80"
                  title={`Secondary Target: ${secondaryCat}`}
                >
                  {secondaryCat}
                </span>
              )}
            </div>
          );
        })()}

        {/* Dosage Box */}
        <div className="p-3 rounded-xl bg-[#020617] border border-slate-800 mb-4 flex items-center justify-between">
          <div className="text-xs text-slate-400 font-medium">Dosage Protocol</div>
          <div className="text-right">
            <div className="text-sm font-extrabold text-sky-400 tracking-wide font-mono">
              {formatDosage()}
            </div>
            {weeklyTotalDosage !== undefined && weeklyTotalDosage !== null && weeklyTotalDosage !== '' && (
              <div className="text-[11px] font-medium text-slate-400 font-mono mt-0.5">
                Weekly: {weeklyTotalDosage} {unit}
              </div>
            )}
          </div>
        </div>

        {/* Details Grid */}
        <div className="space-y-2 text-xs text-slate-300">
          <div className="flex items-center space-x-2">
            <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="text-slate-400">Timing:</span>
            <span className="font-medium text-slate-200">{timingOfDay}</span>
          </div>

          <div className="flex items-center space-x-2">
            <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="text-slate-400">Schedule:</span>
            <span className="font-medium text-slate-200">{frequencySchedule}</span>
          </div>

          <div className="flex items-center space-x-2">
            <Syringe className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="text-slate-400">Format:</span>
            <span className="font-medium text-slate-200">{administrationFormat}</span>
          </div>

          {brandOrSupplier && (
            <div className="flex items-center space-x-2">
              <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span className="text-slate-400">Brand/Supplier:</span>
              <span className="font-medium text-slate-200">
                {obfuscateSupplier ? obfuscateSupplierName(brandOrSupplier) : brandOrSupplier}
              </span>
            </div>
          )}

          {intendedOutcome && (
            <div className="flex items-start space-x-2 pt-1">
              <Target className="w-3.5 h-3.5 text-sky-400 flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-slate-400 mr-1">Outcome:</span>
                <span className="text-slate-300">{intendedOutcome}</span>
              </div>
            </div>
          )}

          {notes && (
            <div className="flex items-start space-x-2 pt-1 text-[11px] text-slate-300 italic bg-[#020617] p-2.5 rounded-lg border border-slate-800/80">
              <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
              <span>{notes}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
        <div>Started: {startDate || 'N/A'}</div>
        {endDate && <div>Ended: {endDate}</div>}
      </div>
    </div>
  );
}

const ExportImageContainer = forwardRef(function ExportImageContainer(
  { protocols = [], options = {}, titleScope = 'Protocol Stack', obfuscateSupplier = false },
  ref
) {
  const shouldObfuscate = Boolean(obfuscateSupplier || options?.obfuscateSupplier);

  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });

  const isSingle = protocols.length === 1;

  return (
    <div
      style={{
        position: 'fixed',
        top: '-99999px',
        left: '-99999px',
        width: isSingle ? '560px' : '980px',
        zIndex: -9999,
        pointerEvents: 'none',
        opacity: 1,
        visibility: 'visible'
      }}
    >
      <div
        ref={ref}
        style={{
          position: 'relative',
          top: 0,
          left: 0,
          width: isSingle ? '520px' : '960px',
          backgroundColor: '#090d16'
        }}
        className="bg-[#090d16] text-white p-7 rounded-3xl border border-slate-800 shadow-2xl font-sans"
      >
      {/* Container Header Banner */}
      <div className="flex items-center justify-between pb-5 border-b border-slate-800 mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-3 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-lg shadow-sky-500/20">
            <Layers className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-lg font-black tracking-tight text-white uppercase font-mono">
              Supps & Peps Protocol Stack
            </h2>
            <p className="text-xs text-slate-400 font-medium">
              {titleScope} • {protocols.length} {protocols.length === 1 ? 'Compound' : 'Compounds'}
            </p>
          </div>
        </div>

        <div className="text-right">
          <div className="text-xs font-semibold text-sky-400 font-mono">
            {currentDate}
          </div>
          <div className="text-[10px] text-slate-500 font-mono">
            Personal Protocol Export
          </div>
        </div>
      </div>

      {/* Grid of Cards */}
      {protocols.length === 0 ? (
        <div className="p-8 text-center bg-[#0f172a] rounded-2xl border border-slate-800">
          <p className="text-xs text-slate-400">No protocol records found for export.</p>
        </div>
      ) : (
        <div
          className={`grid ${
            isSingle
              ? 'grid-cols-1'
              : protocols.length === 2
              ? 'grid-cols-2 gap-5'
              : 'grid-cols-2 lg:grid-cols-3 gap-5'
          }`}
        >
          {protocols.map((protocol) => (
            <OpaqueExportCard
              key={protocol.id || protocol.name}
              protocol={protocol}
              obfuscateSupplier={shouldObfuscate}
            />
          ))}
        </div>
      )}

      {/* Footer Branding */}
      <div className="mt-6 pt-4 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-500 font-mono">
        <div>Generated via Supplement & Peptide Stack Tracker</div>
        <div>Confidential Personal Protocol Log</div>
      </div>
    </div>
  </div>
  );
});

export default ExportImageContainer;
